import React, { useState, useEffect, useCallback } from 'react';
import { FaEye, FaTrash, FaFilter, FaSearch, FaMapMarkerAlt, FaCalendar, FaTag, FaStore, FaUser, FaExclamationTriangle } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import ClienteDetailModal from './ClienteDetailModal';
import TicketsDiscrepanciesAnalysis from './TicketsDiscrepanciesAnalysis';
import { get, del } from '../../../helpers/apiHelper';

const SEGMENTS = [
    { value: 'Sin Compras', label: 'Sin Compras', color: 'gray' },
    { value: 'Nuevo', label: 'Nuevo', color: 'blue' },
    { value: 'Ocasional', label: 'Ocasional', color: 'green' },
    { value: 'Frecuente', label: 'Frecuente', color: 'purple' },
    { value: 'VIP', label: 'VIP', color: 'gold' },
    { value: 'Inactivo', label: 'Inactivo', color: 'red' }
];

const ClientesList = () => {
    const [allClientes, setAllClientes] = useState([]); // Nueva variable para todos los clientes
    const [clientes, setClientes] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showDiscrepanciesAnalysis, setShowDiscrepanciesAnalysis] = useState(false);

    // Estados para filtros
    const [filters, setFilters] = useState({
        search: '',
        segment: '',
        city: '',
        state: '',
        marca: '', // Nuevo filtro para marca
        genero: '', // Nuevo filtro para género
        dateRange: {
            start: '',
            end: ''
        },
        hasTickets: '',
        hasFeedback: ''
    });

    // Estados para estadísticas
    const [stats, setStats] = useState({
        total: 0,
        bySegment: {},
        byCity: {},
        byState: {},
        withTickets: 0,
        withFeedback: 0
    });

    useEffect(() => {
        fetchMarcas();
        fetchClientes();
    }, []);

    // Debounce para los filtros
    useEffect(() => {
        const timer = setTimeout(() => {
            if (allClientes.length > 0) {
                applyFilters();
            }
        }, 300); // 300ms de delay

        return () => clearTimeout(timer);
    }, [filters, allClientes]);

    const fetchMarcas = async () => {
        try {
            const data = await get('marcas/admin');
            setMarcas(data);
        } catch (err) {
            console.error('Error al cargar marcas:', err);
        }
    };

    const fetchClientes = async () => {
        try {
            setLoading(true);
            const data = await get('clientes');
            setAllClientes(data);
            setClientes(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filteredData = allClientes.filter(cliente => {
            // Búsqueda general
            if (filters.search) {
                const searchTerm = filters.search.toLowerCase();
                const searchFields = [
                    cliente?.nombre || '',
                    cliente?.apellido || '',
                    cliente?.email || '',
                    cliente?.telefono || '',
                    cliente?.codigoReferido || ''
                ].map(field => field.toLowerCase());

                if (!searchFields.some(field => field.includes(searchTerm))) {
                    return false;
                }
            }

            // Filtro por género
            if (filters.genero && cliente?.genero !== filters.genero) {
                return false;
            }

            // Filtro por segmento
            if (filters.segment && !cliente?.segments?.some(s => s.value === filters.segment)) {
                return false;
            }

            // Filtro por ciudad
            if (filters.city && (cliente?.geographic_info?.city || '').toLowerCase() !== filters.city.toLowerCase()) {
                return false;
            }

            // Filtro por estado/región
            if (filters.state && (cliente?.geographic_info?.state || '').toLowerCase() !== filters.state.toLowerCase()) {
                return false;
            }

            // Filtro por marca
            if (filters.marca && cliente?.marca !== filters.marca) {
                return false;
            }

            // Filtro por rango de fechas
            if (filters.dateRange.start || filters.dateRange.end) {
                const clientDate = new Date(cliente?.fechaCreacion || null);
                if (filters.dateRange.start && clientDate < new Date(filters.dateRange.start)) {
                    return false;
                }
                if (filters.dateRange.end && clientDate > new Date(filters.dateRange.end)) {
                    return false;
                }
            }

            // Filtro por tickets
            if (filters.hasTickets === 'yes' && (!cliente?.tickets || cliente.tickets.length === 0)) {
                return false;
            }
            if (filters.hasTickets === 'no' && cliente?.tickets && cliente.tickets.length > 0) {
                return false;
            }

            // Filtro por feedback
            if (filters.hasFeedback === 'yes' && (!cliente?.feedbackScores || cliente.feedbackScores.length === 0)) {
                return false;
            }
            if (filters.hasFeedback === 'no' && cliente?.feedbackScores && cliente.feedbackScores.length > 0) {
                return false;
            }

            return true;
        });

        // Calcular estadísticas
        const stats = {
            total: filteredData.length,
            bySegment: {},
            byCity: {},
            byState: {},
            withTickets: filteredData.filter(c => c?.tickets?.length > 0).length,
            withFeedback: filteredData.filter(c => c?.feedbackScores?.length > 0).length
        };

        // Estadísticas por segmento
        filteredData.forEach(cliente => {
            cliente?.segments?.forEach(segment => {
                stats.bySegment[segment.value] = (stats.bySegment[segment.value] || 0) + 1;
            });
            const city = cliente?.geographic_info?.city;
            const state = cliente?.geographic_info?.state;
            if (city) stats.byCity[city] = (stats.byCity[city] || 0) + 1;
            if (state) stats.byState[state] = (stats.byState[state] || 0) + 1;
        });

        setStats(stats);
        setClientes(filteredData);
    };

    const handleDelete = async (clienteId) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar este cliente? Esta acción no se puede deshacer.')) return;

        try {
            await del(`clientes/${clienteId}`);
            await fetchClientes();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDateRangeChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            dateRange: {
                ...prev.dateRange,
                [field]: value
            }
        }));
    };

    if (loading) return <div className="text-center p-4">Cargando...</div>;
    if (error) return <div className="text-red-500 p-4">{error}</div>;

    return (
        <div className="container mx-auto p-4">
            <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">Gestión de Clientes</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowDiscrepanciesAnalysis(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                            title="Analizar discrepancias de puntos en tickets"
                        >
                            <FontAwesomeIcon icon={faChartLine} />
                            <span>Analizar Tickets</span>
                        </button>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                        <FaFilter />
                        {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
                    </button>
                    </div>
                </div>

                {/* Estadísticas rápidas */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500">Total Clientes</div>
                        <div className="text-2xl font-bold">{stats.total}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500">Con Compras</div>
                        <div className="text-2xl font-bold">{stats.withTickets}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500">Con Feedback</div>
                        <div className="text-2xl font-bold">{stats.withFeedback}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500">Ciudades</div>
                        <div className="text-2xl font-bold">{Object.keys(stats.byCity).length}</div>
                    </div>
                </div>

                {/* Barra de búsqueda */}
                <div className="relative mb-4">
                    <input
                        type="text"
                        placeholder="Buscar por nombre, email, teléfono..."
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>

                {/* Filtros avanzados */}
                {showFilters && (
                    <div className="bg-white p-4 rounded-lg shadow mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Filtro de marca */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <FaStore className="inline mr-2" />
                                    Marca
                                </label>
                                <select
                                    className="w-full border rounded-lg p-2"
                                    value={filters.marca}
                                    onChange={(e) => handleFilterChange('marca', e.target.value)}
                                >
                                    <option value="">Todas las marcas</option>
                                    {marcas.map(marca => (
                                        <option key={marca._id} value={marca._id}>
                                            {marca.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Filtros de segmento */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <FaTag className="inline mr-2" />
                                    Segmento
                                </label>
                                <select
                                    className="w-full border rounded-lg p-2"
                                    value={filters.segment}
                                    onChange={(e) => handleFilterChange('segment', e.target.value)}
                                >
                                    <option value="">Todos los segmentos</option>
                                    {SEGMENTS.map(segment => (
                                        <option key={segment.value} value={segment.value}>
                                            {segment.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Filtros de ubicación */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <FaUser className="inline mr-2" />
                                    Género
                                </label>
                                <select
                                    className="w-full border rounded-lg p-2"
                                    value={filters.genero}
                                    onChange={(e) => handleFilterChange('genero', e.target.value)}
                                >
                                    <option value="">Todos los géneros</option>
                                    <option value="HOMBRE">Hombre</option>
                                    <option value="MUJER">Mujer</option>
                                    <option value="NO_BINARIO">No binario</option>
                                    <option value="PREFIERO_NO_DECIRLO">Prefiero no decirlo</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <FaMapMarkerAlt className="inline mr-2" />
                                    Ciudad
                                </label>
                                <input
                                    type="text"
                                    className="w-full border rounded-lg p-2"
                                    placeholder="Filtrar por ciudad"
                                    value={filters.city}
                                    onChange={(e) => handleFilterChange('city', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <FaMapMarkerAlt className="inline mr-2" />
                                    Región/Estado
                                </label>
                                <input
                                    type="text"
                                    className="w-full border rounded-lg p-2"
                                    placeholder="Filtrar por región"
                                    value={filters.state}
                                    onChange={(e) => handleFilterChange('state', e.target.value)}
                                />
                            </div>

                            {/* Filtros de fecha */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <FaCalendar className="inline mr-2" />
                                    Fecha desde
                                </label>
                                <input
                                    type="date"
                                    className="w-full border rounded-lg p-2"
                                    value={filters.dateRange.start}
                                    onChange={(e) => handleDateRangeChange('start', e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <FaCalendar className="inline mr-2" />
                                    Fecha hasta
                                </label>
                                <input
                                    type="date"
                                    className="w-full border rounded-lg p-2"
                                    value={filters.dateRange.end}
                                    onChange={(e) => handleDateRangeChange('end', e.target.value)}
                                />
                            </div>

                            {/* Otros filtros */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Compras
                                </label>
                                <select
                                    className="w-full border rounded-lg p-2"
                                    value={filters.hasTickets}
                                    onChange={(e) => handleFilterChange('hasTickets', e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="yes">Con compras</option>
                                    <option value="no">Sin compras</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Feedback
                                </label>
                                <select
                                    className="w-full border rounded-lg p-2"
                                    value={filters.hasFeedback}
                                    onChange={(e) => handleFilterChange('hasFeedback', e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="yes">Con feedback</option>
                                    <option value="no">Sin feedback</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Tabla de clientes */}
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full table-auto">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marca</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Segmento</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actividad</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {clientes.map((cliente) => (
                            <tr key={cliente._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                        {cliente?.nombre || 'No disponible'} {cliente?.apellido || ''}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        Ref: {cliente?.codigoReferido || 'No disponible'}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        {cliente?.genero === 'HOMBRE' && 'Hombre'}
                                        {cliente?.genero === 'MUJER' && 'Mujer'}
                                        {cliente?.genero === 'NO_BINARIO' && 'No binario'}
                                        {cliente?.genero === 'PREFIERO_NO_DECIRLO' && 'Prefiero no decirlo'}
                                        {!cliente?.genero && 'No especificado'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {marcas.find(m => m._id === cliente?.marca)?.nombre || 'No asignada'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {cliente?.geographic_info?.city || 'No disponible'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {cliente?.geographic_info?.state || 'No disponible'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{cliente?.email || 'No disponible'}</div>
                                    <div className="text-sm text-gray-500">{cliente?.telefono || 'No disponible'}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {cliente?.segments?.map((segment, index) => (
                                        <span
                                            key={index}
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${segment?.color || 'gray'}-100 text-${segment?.color || 'gray'}-800`}
                                        >
                                            {segment?.label || 'Sin segmento'}
                                        </span>
                                    )) || <span className="text-gray-500">Sin segmento</span>}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {cliente?.tickets?.length || 0} compras
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {cliente?.feedbackScores?.length || 0} feedback
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex space-x-3">
                                        <button
                                            onClick={() => setSelectedCliente(cliente)}
                                            className="text-blue-600 hover:text-blue-900"
                                            title="Ver detalles"
                                        >
                                            <FaEye className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(cliente._id)}
                                            className="text-red-600 hover:text-red-900"
                                            title="Eliminar cliente"
                                        >
                                            <FaTrash className="h-5 w-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedCliente && (
                <ClienteDetailModal
                    cliente={selectedCliente}
                    onClose={() => setSelectedCliente(null)}
                />
            )}

            {showDiscrepanciesAnalysis && (
                <TicketsDiscrepanciesAnalysis
                    clienteId={selectedCliente?._id || null}
                    onClose={() => setShowDiscrepanciesAnalysis(false)}
                />
            )}
        </div>
    );
};

export default ClientesList; 